/**
 * The Asistente drawer's width: a drag handle on its left edge (double-click resets), remembered in this
 * browser (localStorage "assistant-width"), and an "Ocultar" button in the panel that closes the drawer.
 * The width only applies while the Asistente tab is the one shown (CSS --assistant-width); phones keep
 * the full-width drawer.
 */
import type { ViewerShell } from '../../shell';

const STORAGE_KEY = 'assistant-width';
export const DEFAULT_WIDTH = 500;  // fits 16 px text without crowding
export const MIN_WIDTH = 320;
const MAX_FRACTION = 0.7; // never more than 70 % of the window: the image must stay readable
const KEY_STEP = 20;

function maxWidth(): number {
  return Math.max(MIN_WIDTH, Math.floor(window.innerWidth * MAX_FRACTION));
}

export function clampWidth(width: number): number {
  return Math.round(Math.min(maxWidth(), Math.max(MIN_WIDTH, width)));
}

function readWidth(): number {
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? clampWidth(value) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function storeWidth(width: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch (error) {
    console.warn('[viewer] assistant width not persisted', error);
  }
}

/** Installs the handle on the drawer and applies the remembered width. */
export function installResize(shell: ViewerShell): void {
  const drawer = shell.regions.rightDrawer;
  const body = drawer.parentElement as HTMLElement;
  const apply = (width: number) => body.style.setProperty('--assistant-width', `${width}px`);
  let width = readWidth();
  apply(width);

  const handle = document.createElement('div');
  handle.className = 'assistant-resize';
  handle.setAttribute('role', 'separator');
  handle.setAttribute('aria-orientation', 'vertical');
  handle.setAttribute('aria-label', 'Ancho del Asistente Guru');
  handle.title = 'Arrastre para cambiar el ancho (doble clic: ancho inicial)';
  handle.tabIndex = 0;
  const set = (next: number, persist: boolean) => {
    width = clampWidth(next);
    apply(width);
    handle.setAttribute('aria-valuenow', String(width));
    if (persist) storeWidth(width);
  };
  handle.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    const right = drawer.getBoundingClientRect().right;
    const move = (e: PointerEvent) => set(right - e.clientX, false);
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.classList.remove('dragging');
      storeWidth(width);
    };
    handle.classList.add('dragging');
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
  });
  handle.addEventListener('dblclick', () => set(DEFAULT_WIDTH, true));
  handle.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') set(width + KEY_STEP, true);
    else if (event.key === 'ArrowRight') set(width - KEY_STEP, true);
    else return;
    event.preventDefault();
  });
  window.addEventListener('resize', () => set(width, false));
  drawer.appendChild(handle);
}

/** "Ocultar" in the panel banner: closes the drawer (the top-bar Asistente button brings it back). */
export function addHideButton(panelRoot: HTMLElement, onHide: () => void): void {
  const banner = panelRoot.querySelector('.assistant-banner');
  if (!banner) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'assistant-hide';
  button.textContent = 'Ocultar';
  button.title = 'Ocultar el Asistente Guru';
  button.addEventListener('click', onHide);
  banner.appendChild(button);
}
